import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Eye, MessageSquare, ClipboardCheck } from "lucide-react";
import { breadcrumbSchema } from "@/components/Seo";
import PageSeo from "@/components/PageSeo";
import PageHeader from "@/components/public/PageHeader";
import { Reveal, SectionHeading } from "@/components/public/Primitives";
import { useLanguage } from "@/context/LanguageContext";
import { WARD_PRIORITIES } from "@/data/constants";

export default function GbaVision() {
  const { tr, lang } = useLanguage();
  const pillars = [
    { icon: Eye, title: "Citizen-First Governance", desc: "Leadership that listens first and stays reachable to every resident of the ward." },
    { icon: MessageSquare, title: "Public Grievance System", desc: "A simple, structured way for citizens to raise local problems and track progress." },
    { icon: ClipboardCheck, title: "Transparent Follow-Up", desc: "Honest updates on issues — from received, to in-progress, to resolved." },
    { icon: ShieldCheck, title: "Local Development Agenda", desc: "Ward-level focus on roads, drainage, water, waste, lighting, parks, health and education." },
  ];

  return (
    <div data-testid="gba-vision-page">
      <PageSeo
        slug="gba-vision"
        title="A Citizen-First Vision for Hoodi Ward 29 | Girish Kumar Naidu"
        description="Girish Kumar Naidu's citizen-first vision for GBA Hoodi Ward 29 — accessible leadership, ward-level development, transparent grievance follow-up."
        keywords="Girish Kumar Naidu GBA corporator aspirant, Greater Bengaluru Authority election, Hoodi ward development, Bengaluru civic leadership"
        schemas={[breadcrumbSchema([{ name: "Home", url: window.location.origin }, { name: "Hoodi Ward Vision" }])]}
      />
      <PageHeader
        overline={tr("page.vision.overline")}
        title={tr("page.vision.title")}
        subtitle={tr("page.vision.subtitle")}
        crumbs={[{ name: "Hoodi Ward Vision" }]}
      />

      {/* Vision intro */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <Reveal>
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
              As Bengaluru enters a new phase of civic governance under the Greater Bengaluru Authority, the
              opportunity is clear: leadership that is close to citizens, responsive to everyday problems, and
              honest about progress. This is a service-focused vision — built on years of grassroots work — to make
              ward-level governance accessible to every resident.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-paper py-16 md:py-24 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            {pillars.map((p, i) => (
              <Reveal key={i} delay={(i % 2) * 0.08}>
                <div className="bg-white rounded-2xl p-8 border border-gray-200 card-lift h-full">
                  <div className="flex items-start justify-between mb-5">
                    <div className="h-12 w-12 rounded-xl bg-navy flex items-center justify-center">
                      <p.icon className="h-6 w-6 text-gold" />
                    </div>
                    <span className="font-mono text-xs text-gray-300 font-bold">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="font-display text-xl font-black text-navy mb-2">{p.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Ward priorities */}
      <section className="bg-navy py-16 md:py-24 grain">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <SectionHeading light center overline="Ward-Level Priorities" title="A Practical Local Development Agenda" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {WARD_PRIORITIES.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.05}>
                <div className="glass-dark rounded-2xl p-6 flex items-start gap-4 hover:border-gold/40 transition-colors h-full">
                  <div className="h-11 w-11 rounded-xl bg-gold flex items-center justify-center shrink-0">
                    <p.icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-white">{(lang === "kn" && p.title_kn) || p.title}</h3>
                    <p className="text-sm text-slate-300 mt-1 leading-relaxed">{(lang === "kn" && p.desc_kn) || p.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gold py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-black text-white tracking-tight">Be Part of the Movement</h2>
          <p className="mt-4 text-white/90 text-lg font-medium">Share your ward issue or join hands to build a better Hoodi Ward 29.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/submit-ward-issue" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-navy text-white font-bold hover:bg-navy-800 transition-colors">
              Submit Ward Issue <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-navy font-bold hover:bg-paper transition-colors">
              Join the Movement
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
